import type { CSSProperties, FC, ReactNode } from 'react'

import type { LucideIcon } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Reveal from '@/components/motion/reveal'
import PixelText from '@/components/motion/pixel-text'
import PageHero from '@/components/generals/page-hero'
import ButtonLink from '@/components/generals/button-link'
import FinalCta from '@/components/landing/final-cta'

import {
    Activity,
    BadgeCheck,
    Eye,
    GitPullRequest,
    Globe,
    KeyRound,
    ListOrdered,
    Lock,
    MessagesSquare,
    Network,
    NotebookText,
    ScrollText,
    ShieldCheck,
    Waves
} from 'lucide-react'

interface Stage {
    number: string
    icon: LucideIcon
    title: string
    body: string
    points: string[]
}

const stages: Stage[] = [
    {
        number: '01',
        icon: Network,
        title: 'Map',
        body: 'Forge reads your repositories, cloud accounts and telemetry to draw a living System Map of every service and the paths between them.',
        points: ['Redrawn on every deploy', 'Ownership from code owners files', 'Dependencies from real traffic']
    },
    {
        number: '02',
        icon: Waves,
        title: 'Triage',
        body: 'Every alert, page, ticket and deploy is read in context. Related signals become one incident, and noise is retired with a reason.',
        points: ['Groups signals by shared cause', 'Explains every muted alert', 'Works across all of your tools']
    },
    {
        number: '03',
        icon: ListOrdered,
        title: 'Prioritize',
        body: 'Incidents and optimizations land in one ranked queue, ordered by blast radius, urgency and what each one costs you today.',
        points: ['Customer impact first', 'Savings priced in your currency', 'Respects your team decisions']
    },
    {
        number: '04',
        icon: GitPullRequest,
        title: 'Resolve',
        body: 'Forge drafts the code or configuration change, routes it to the right reviewers and watches production after release to prove it held.',
        points: ['Nothing ships without approval', 'Canary checks after every merge', 'A clear record of each fix']
    }
]

interface Capability {
    icon: LucideIcon
    title: string
    body: string
}

const capabilities: Capability[] = [
    { icon: Eye, title: 'Watches', body: 'Scheduled agents that hunt for slow-burning risks: certificates near expiry, creeping latency, costs drifting upward.' },
    { icon: NotebookText, title: 'Decision memory', body: 'Every call your team makes is remembered, so recommendations never undo something you chose on purpose.' },
    { icon: MessagesSquare, title: 'Works in your channels', body: 'Forge joins incident threads in the team chat you already use and answers questions in plain language.' },
    { icon: Activity, title: 'Root cause, with receipts', body: 'Each finding links to the traces, metrics and changes that support it, so you can check the reasoning in seconds.' },
    { icon: GitPullRequest, title: 'Fixes as pull requests', body: 'Changes arrive as reviewable pull requests with a written explanation, a rollback plan and the tests that cover them.' },
    { icon: BadgeCheck, title: 'Verified outcomes', body: 'After release Forge compares the numbers that mattered against their baseline and reopens anything that slips.' }
]

const sources = [
    'Metrics',
    'Traces',
    'Logs',
    'Paging',
    'Source control',
    'CI and deploys',
    'Cloud accounts',
    'Team chat',
    'Ticketing',
    'Feature flags',
    'Status pages',
    'Runbooks'
]

const trust: Capability[] = [
    { icon: Lock, title: 'Read-only by default', body: 'Forge observes with scoped, read-only access. Write access is opt-in, per system.' },
    { icon: ShieldCheck, title: 'SOC 2 Type II', body: 'Audited every year, with GDPR and HIPAA programs in place.' },
    { icon: KeyRound, title: 'SSO and SCIM', body: 'Sign in through your identity provider and manage access in one place.' },
    { icon: ScrollText, title: 'Full audit log', body: 'Every question, finding and change is recorded and exportable.' },
    { icon: Globe, title: 'Data residency', body: 'Run in the EU or the US, with customer data kept in region.' },
    { icon: GitPullRequest, title: 'Human approval', body: 'Nothing reaches production until someone on your team approves it.' }
]

const ProductPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Product'
                description='How Forge maps your production systems, turns alert noise into clear incidents, ranks what matters and ships verified fixes for your review.'
            />

            <PageHero
                eyebrow='Product'
                title='One engineer for all of production.'
                description='Forge connects to the tools you already run, learns how your systems fit together and works every incident from first alert to verified fix.'
            >
                <div className='intro-rise flex flex-wrap gap-3 pt-3' style={{ '--intro-delay': '640ms' } as CSSProperties}>
                    <ButtonLink href='/demo' size='lg' arrow className='h-12 px-6 text-[16px]'>
                        Book a demo
                    </ButtonLink>

                    <ButtonLink href='/pricing' variant='secondary' size='lg' className='h-12 px-6 text-[16px]'>
                        See pricing
                    </ButtonLink>
                </div>
            </PageHero>

            <section aria-labelledby='loop-title' data-header-theme='dark' className='bg-night text-night-ink'>
                <div className='container-page flex flex-col gap-12 py-24 sm:gap-16 sm:py-32'>
                    <div className='grid gap-6 lg:grid-cols-2 lg:items-end'>
                        <PixelText
                            id='loop-title'
                            as='h2'
                            text='The full loop, in one place.'
                            className='max-w-[30rem] font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-white text-balance'
                        />

                        <Reveal delay={120}>
                            <p className='max-w-[34rem] text-pretty text-[17px] leading-relaxed text-night-muted sm:text-[18px]'>
                                Most tools stop at the alert. Forge carries the work all the way through: it understands the system, finds the cause, writes the change and confirms the outcome.
                            </p>
                        </Reveal>
                    </div>

                    <ol className='grid gap-3 md:grid-cols-2 lg:grid-cols-4'>
                        {stages.map((stage, index) => (
                            <Reveal as='li' key={stage.title} index={index} className='flex'>
                                <div className='night-card flex w-full flex-col gap-6 p-6 sm:p-7'>
                                <span className='flex items-center justify-between'>
                                    <span className='grid size-10 place-items-center rounded-[10px] bg-white/[0.06] text-night-ink'>
                                        <stage.icon className='size-5' strokeWidth={1.6} aria-hidden='true' />
                                    </span>

                                    <span className='font-mono text-[12px] text-night-subtle'>
                                        {stage.number}
                                    </span>
                                </span>

                                <div className='flex flex-col gap-3'>
                                    <PixelText as='h3' text={stage.title} className='font-heading text-[1.7rem] font-normal leading-none tracking-[-0.02em] text-white' />

                                    <p className='text-pretty text-[15px] leading-relaxed text-night-muted'>
                                        {stage.body}
                                    </p>
                                </div>

                                <ul className='mt-auto flex flex-col gap-2 border-t border-dashed border-night-line pt-5'>
                                    {stage.points.map((point) => (
                                        <li key={point} className='flex items-center gap-2.5 text-[13.5px] text-night-ink/85'>
                                            <span className='dot size-[5px] text-improve' />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                                </div>
                            </Reveal>
                        ))}
                    </ol>
                </div>
            </section>

            <section aria-labelledby='capabilities-title' data-header-theme='light' className='border-b border-dashed border-line bg-paper'>
                <div className='container-page flex flex-col gap-12 py-24 sm:gap-16 sm:py-32'>
                    <PixelText
                        id='capabilities-title'
                        as='h2'
                        text='Built for the way your team already works.'
                        className='max-w-[34rem] font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink text-balance'
                    />

                    <ul className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                        {capabilities.map((item, index) => (
                            <Reveal as='li' key={item.title} index={index % 3} className='flex'>
                                <div className='card flex w-full flex-col gap-4 p-6 sm:p-7'>
                                <span className='grid size-10 place-items-center rounded-[10px] bg-ink/[0.05] text-ink'>
                                    <item.icon className='size-5' strokeWidth={1.6} aria-hidden='true' />
                                </span>

                                <PixelText as='h3' text={item.title} className='text-[1.2rem] font-medium tracking-[-0.01em] text-ink' />

                                <p className='text-pretty text-[15px] leading-relaxed text-ink-muted'>
                                    {item.body}
                                </p>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-labelledby='sources-title' data-header-theme='light' className='border-b border-dashed border-line bg-paper-lifted'>
                <div className='container-page grid gap-12 py-24 sm:py-32 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] lg:gap-16'>
                    <div className='flex flex-col gap-6'>
                        <PixelText
                            id='sources-title'
                            as='h2'
                            text='Reads every tool you run.'
                            className='max-w-[26rem] font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-ink text-balance'
                        />

                        <Reveal delay={120}>
                            <p className='max-w-[30rem] text-pretty text-[17px] leading-relaxed text-ink-muted'>
                                Connect your stack in an afternoon. Forge starts with read-only access, builds its map within the first day and sends its first findings within the first week.
                            </p>
                        </Reveal>
                    </div>

                    <Reveal variant='scale' delay={80}>
                        <ul className='grid grid-cols-2 gap-2.5 sm:grid-cols-3'>
                            {sources.map((source) => (
                                <li key={source} className='card flex h-16 items-center gap-3 px-4 text-[15px] text-ink'>
                                    <span className='dot size-[6px] text-healthy' />
                                    {source}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            <section aria-labelledby='trust-title' data-header-theme='dark' className='bg-night text-night-ink'>
                <div className='container-page flex flex-col gap-12 py-24 sm:gap-16 sm:py-32'>
                    <div className='grid gap-6 lg:grid-cols-2 lg:items-end'>
                        <PixelText
                            id='trust-title'
                            as='h2'
                            text='Trusted with production.'
                            className='max-w-[28rem] font-heading text-[clamp(2.2rem,4.4vw,3.4rem)] font-normal leading-[1.06] tracking-[-0.025em] text-white text-balance'
                        />

                        <Reveal delay={120}>
                            <p className='max-w-[34rem] text-pretty text-[17px] leading-relaxed text-night-muted sm:text-[18px]'>
                                Forge was designed by engineers who have carried the pager. Access is narrow, every action is recorded and your team stays in control of every change.
                            </p>
                        </Reveal>
                    </div>

                    <ul className='grid gap-px overflow-hidden rounded-2xl bg-night-line sm:grid-cols-2 lg:grid-cols-3'>
                        {trust.map((item) => (
                            <li key={item.title} className='flex flex-col gap-3 bg-night p-6 sm:p-8'>
                                <item.icon className='size-5 text-night-ink' strokeWidth={1.6} aria-hidden='true' />

                                <PixelText as='h3' text={item.title} className='text-[1.1rem] font-medium text-white' />

                                <p className='text-pretty text-[14.5px] leading-relaxed text-night-muted'>
                                    {item.body}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <FinalCta />
        </Layout>
    )
}

export default ProductPage
